<script setup lang="ts">
import { computed, useAttrs } from 'vue';

import analytics from '@/analytics';

interface Props {
  id: string;
  type: 'primary' | 'secondary' | 'ghost' | 'danger' | 'warning' | 'success' | 'text' | 'link' | 'icon' | 'primary-alt';
  onClick?: (e?: Event) => void;
  size?: 'x-small' | 'small' | 'medium' | 'large';
  rounded?: 'none' | 'small' | 'medium' | 'large';
  disabled?: boolean;
  loading?: boolean;
  analyticsEvent?: string;
  analyticsProperties?: Record<string, any>;
  customStyle?: string;
  useAnchorTag?: boolean;
  anchorTagProps?: Record<string, any>;
  tooltip?: string;
  tooltipPosition?:
    | 'tooltip-bottom-left'
    | 'tooltip-bottom-right'
    | 'tooltip-top-center'
    | 'default'
    | 'tooltip-top-right';
  ariaLabel?: string;
  nativeType?: 'button' | 'submit' | 'reset';
}

defineOptions({
  inheritAttrs: false
});

const props = withDefaults(defineProps<Props>(), {
  size: 'medium',
  analyticsEvent: 'cta-clicked',
  customStyle: '',
  useAnchorTag: false,
  tooltipPosition: 'default',
  nativeType: 'button'
});

const attrs = useAttrs();

const anchorAttributes = computed(() => {
  const attributes = { ...attrs, ...props.anchorTagProps };
  if (!props.disabled && !props.loading) {
    return attributes;
  }

  ['href', 'target', 'download', 'ping', 'rel', 'hreflang', 'referrerpolicy'].forEach(attribute => {
    delete attributes[attribute];
  });
  return attributes;
});

const emit = defineEmits<{
  (e: 'click', event?: Event): void;
}>();

const getButtonStyle = () => {
  switch (props.type) {
    case 'primary':
      return 'bg-themePrimaryColor text-themePrimaryTextColor ';
    case 'primary-alt':
      return 'bg-themePrimaryTextColor text-themePrimaryColor brightness-90';
    case 'secondary':
      return 'bg-themeSecondaryColor text-themeSecondaryTextColor hover:brightness-125';
    case 'ghost':
      return 'border border-themePrimaryTextColor/20 bg-transparent text-themePrimaryTextColor ';
    case 'danger':
      return 'bg-danger text-white';
    case 'warning':
      return 'bg-warning text-white';
    case 'success':
      return 'bg-success text-white';
    case 'text':
      return 'bg-transparent text-primary';
    case 'link':
      return 'bg-transparent text-themePrimaryTextColor underline !p-0';
    default:
      return 'bg-primary text-white';
  }
};

const getButtonRoundedStyle = () => {
  switch (props.rounded) {
    case 'none':
      return '';
    case 'small':
      return 'rounded-sm';
    case 'medium':
      return 'rounded-md';
    case 'large':
      return 'rounded-lg';
    default:
      return 'rounded';
  }
};

const buttonClasses = computed(() => {
  const baseClass =
    'inline-flex min-h-6 min-w-6 items-center justify-center btn-text transition-colors brightness-[0.85] hover:brightness-100 focus:brightness-100 focus-visible:ring-2 focus-visible:ring-themePrimaryTextColor focus-visible:ring-offset-2 ';

  const sizeClass = {
    'x-small': 'p-0 text-3xs',
    small: 'px-2 py-1 text-2xs',
    medium: 'px-2 py-1 text-2xs md:px-4 md:py-2 md:text-xs',
    large: 'px-6 py-3 '
  }[props.size];

  const disabledClass = props.disabled ? 'brightness-50 cursor-not-allowed' : '';

  return `${baseClass} ${getButtonStyle()} ${getButtonRoundedStyle()} ${sizeClass} ${disabledClass}`;
});

const accessibleLabel = computed(() => props.ariaLabel || (attrs['aria-label'] as string | undefined));

const handleClick = (e?: Event) => {
  if (props.disabled || props.loading) {
    e?.preventDefault();
    return;
  }

  // TODO: Remove this and only use the emit when the button is clicked @Pankaj
  if (props.onClick && typeof props.onClick === 'function') {
    props.onClick(e);
  } else {
    emit('click', e);
  }
  analytics.track(props.analyticsEvent, { id: props.id, ...props.analyticsProperties });
};
</script>

<template>
  <!-- Native anchors synthesize click from Enter; a key handler would invoke the action twice. -->
  <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions, vuejs-accessibility/click-events-have-key-events -->
  <a
    v-if="useAnchorTag"
    v-bind="anchorAttributes"
    :id="id"
    :data-testid="id"
    :class="{ [buttonClasses]: true, [props.customStyle]: !!props.customStyle, [tooltipPosition]: !!tooltipPosition }"
    :aria-disabled="disabled || loading || undefined"
    :tabindex="disabled || loading ? -1 : undefined"
    :data-tooltip="tooltip"
    data-tooltip-multiline="true"
    :aria-label="accessibleLabel"
    @click="handleClick"
  >
    <slot />
  </a>
  <button
    v-else
    v-bind="$attrs"
    :id="id"
    :data-testid="id"
    :class="{ [buttonClasses]: true, [props.customStyle]: !!props.customStyle, [tooltipPosition]: !!tooltipPosition }"
    :disabled="disabled || loading"
    :type="nativeType"
    :data-tooltip="tooltip"
    data-tooltip-multiline="true"
    :aria-label="accessibleLabel"
    @click="handleClick"
  >
    <slot />
  </button>
</template>
